import React, { useEffect, useRef, useState } from "react";
import {
  MdMic,
  MdStop,
  MdSend,
  MdRefresh,
  MdContentCopy,
  MdCheck,
  MdGraphicEq,
  MdInfoOutline,
} from "react-icons/md";
import { generateVoiceNote, VoiceNoteReport } from "api/ai";

interface VoiceNoteGenerationProps {
  catalog: Record<string, any[]>;
  onGenerated: (report: VoiceNoteReport) => void;
  onCreateNote: () => void;
  onCancel: () => void;
}

const VoiceNoteGeneration: React.FC<VoiceNoteGenerationProps> = ({
  catalog,
  onGenerated,
  onCreateNote,
  onCancel,
}) => {
  const recognitionRef = useRef<any>(null);
  const shouldListenRef = useRef(false);
  const restartTimeoutRef = useRef<any>(null);
  const cycleTimeoutRef = useRef<any>(null);
  const watchdogIntervalRef = useRef<any>(null);

  const transcriptRef = useRef("");
  const lastSpeechTimeRef = useRef<number>(Date.now());
  const lastResultTimeRef = useRef<number>(Date.now());
  const sessionStartRef = useRef<number>(Date.now());
  const sessionFinalCountRef = useRef<number>(0);
  const conversationModeRef = useRef<boolean>(true);
  const languageRef = useRef<string>("en-US");
  const isAudioActiveRef = useRef<boolean>(false);

  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isAudioActive, setIsAudioActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState<number>(0);

  // Synchronize state with refs
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    isAudioActiveRef.current = isAudioActive;
  }, [isAudioActive]);

  // Session duration timer
  useEffect(() => {
    let interval: any = null;
    if (isListening) {
      interval = setInterval(() => {
        setDurationSeconds((sec) => sec + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldListenRef.current = false;
      clearTimeout(restartTimeoutRef.current);
      clearTimeout(cycleTimeoutRef.current);
      if (watchdogIntervalRef.current) {
        clearInterval(watchdogIntervalRef.current);
        watchdogIntervalRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onresult = null;
          recognitionRef.current.abort();
        } catch (e) {}
        recognitionRef.current = null;
      }
    };
  }, []);

  // Format incoming speech with proper capitalization and speaker turn separation
  const appendFormattedSpeech = (
    currentText: string,
    chunk: string,
    isSpeakerTurn: boolean
  ): string => {
    const trimmedChunk = chunk.trim();
    if (!trimmedChunk) return currentText;

    const capitalized =
      trimmedChunk.charAt(0).toUpperCase() + trimmedChunk.slice(1);

    const trimmedCurrent = currentText.trim();
    if (!trimmedCurrent) {
      return capitalized;
    }

    if (isSpeakerTurn) {
      const endsWithPunct = /[.?!]$/.test(trimmedCurrent);
      return `${trimmedCurrent}${endsWithPunct ? "" : "."}\n\n${capitalized}`;
    }

    const lastChar = trimmedCurrent.slice(-1);
    if ([".", "!", "?"].includes(lastChar)) {
      return `${trimmedCurrent} ${capitalized}`;
    }
    return `${trimmedCurrent} ${trimmedChunk}`;
  };

  // Gracefully cycles the recognition session to prevent Chrome's internal 60-second / 8-line stream limit from stalling
  const cycleRecognitionSession = () => {
    if (!shouldListenRef.current) return;

    // If there is any uncommitted interim speech during a cycle, commit it now so nothing is lost
    setInterimTranscript((pendingInterim) => {
      if (pendingInterim.trim()) {
        const updated = appendFormattedSpeech(
          transcriptRef.current,
          pendingInterim.trim(),
          false
        );
        transcriptRef.current = updated;
        setTranscript(updated);
      }
      return "";
    });

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.abort();
      } catch (e) {}
      recognitionRef.current = null;
    }

    clearTimeout(restartTimeoutRef.current);
    restartTimeoutRef.current = setTimeout(() => {
      if (shouldListenRef.current) {
        startRecognitionSession();
      }
    }, 40);
  };

  // Watchdog monitor: detects stalled Google Speech connections and auto-refreshes long-running sessions
  const startWatchdog = () => {
    if (watchdogIntervalRef.current) {
      clearInterval(watchdogIntervalRef.current);
    }

    watchdogIntervalRef.current = setInterval(() => {
      if (!shouldListenRef.current) return;

      const now = Date.now();
      const sessionAge = now - sessionStartRef.current;
      const timeSinceLastResult = now - lastResultTimeRef.current;

      // Condition 1: Long session refresh. Chrome Speech API sockets degrade after ~30-40 seconds.
      // If session is >25s old and there is a pause (>1s of no results), or session is >40s old:
      if (sessionAge > 40000 || (sessionAge > 25000 && timeSinceLastResult > 1200)) {
        cycleRecognitionSession();
        return;
      }

      // Condition 2: Zombie stall recovery. Sound/speech was detected, but no transcriptions received for >4.5 seconds
      if (isAudioActiveRef.current && timeSinceLastResult > 4500) {
        console.warn("Speech recognition stream stall detected. Re-spawning clean session...");
        cycleRecognitionSession();
        return;
      }
    }, 1500);
  };

  const stopWatchdog = () => {
    if (watchdogIntervalRef.current) {
      clearInterval(watchdogIntervalRef.current);
      watchdogIntervalRef.current = null;
    }
  };

  // Initialize a fresh continuous SpeechRecognition session
  const startRecognitionSession = () => {
    if (!shouldListenRef.current) return;

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError(
        "Voice transcription is not supported in this browser. Please use Chrome or Edge."
      );
      shouldListenRef.current = false;
      setIsListening(false);
      return;
    }

    // Safely detach previous instance
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.abort();
      } catch (e) {}
      recognitionRef.current = null;
    }

    sessionStartRef.current = Date.now();
    sessionFinalCountRef.current = 0;
    lastResultTimeRef.current = Date.now();

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = languageRef.current || "en-US";
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setIsAudioActive(true);
      };

      recognition.onsoundstart = () => {
        setIsAudioActive(true);
      };

      recognition.onspeechstart = () => {
        setIsAudioActive(true);
        setIsSpeaking(true);
      };

      recognition.onspeechend = () => {
        setIsSpeaking(false);
      };

      recognition.onsoundend = () => {
        setIsAudioActive(false);
      };

      recognition.onresult = (event: any) => {
        lastResultTimeRef.current = Date.now();
        let finalChunk = "";
        let interimChunk = "";

        // Extract final text from resultIndex onwards
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const item = event.results[i];
          if (item.isFinal) {
            const text = item[0]?.transcript || "";
            finalChunk += (finalChunk ? " " : "") + text.trim();
          }
        }

        // Always scan all items for complete interim text
        for (let i = 0; i < event.results.length; i += 1) {
          const item = event.results[i];
          if (!item.isFinal) {
            const text = item[0]?.transcript || "";
            interimChunk += (interimChunk ? " " : "") + text.trim();
          }
        }

        if (finalChunk.trim()) {
          const now = Date.now();
          const pauseDuration = now - lastSpeechTimeRef.current;
          lastSpeechTimeRef.current = now;

          // Conversational pause of >1.3s indicates a new speaker turn
          const isTurnBreak =
            conversationModeRef.current &&
            pauseDuration > 1300 &&
            transcriptRef.current.trim().length > 0;

          const updated = appendFormattedSpeech(
            transcriptRef.current,
            finalChunk.trim(),
            isTurnBreak
          );

          transcriptRef.current = updated;
          setTranscript(updated);

          // Rolling buffer management: after accumulating 4-5 final chunks, schedule a seamless cycle at the next natural pause (700ms)
          sessionFinalCountRef.current += 1;
          if (sessionFinalCountRef.current >= 4) {
            clearTimeout(cycleTimeoutRef.current);
            cycleTimeoutRef.current = setTimeout(() => {
              if (shouldListenRef.current) {
                cycleRecognitionSession();
              }
            }, 700);
          }
        }

        setInterimTranscript(interimChunk.trim());
      };

      recognition.onerror = (event: any) => {
        console.warn("Speech recognition notice:", event.error);
        if (event.error === "no-speech") {
          return;
        }
        if (event.error === "aborted") {
          return;
        }
        if (
          event.error === "not-allowed" ||
          event.error === "service-not-allowed"
        ) {
          setError(
            "Microphone permission was denied. Please allow microphone access in your browser address bar."
          );
          shouldListenRef.current = false;
          setIsListening(false);
          stopWatchdog();
          return;
        }
        if (event.error === "audio-capture" || event.error === "network") {
          // Auto-recover transient network or audio capture glitches
          if (shouldListenRef.current) {
            clearTimeout(restartTimeoutRef.current);
            restartTimeoutRef.current = setTimeout(() => {
              if (shouldListenRef.current) {
                startRecognitionSession();
              }
            }, 300);
          }
          return;
        }
      };

      recognition.onend = () => {
        setIsAudioActive(false);
        setIsSpeaking(false);

        // Keep-alive loop across pauses or Chrome utterance boundaries
        if (shouldListenRef.current) {
          clearTimeout(restartTimeoutRef.current);
          restartTimeoutRef.current = setTimeout(() => {
            if (shouldListenRef.current) {
              startRecognitionSession();
            }
          }, 50);
        } else {
          setIsListening(false);
          setInterimTranscript("");
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.warn("Recognition start attempt error:", err);
      if (shouldListenRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = setTimeout(() => {
          if (shouldListenRef.current) {
            startRecognitionSession();
          }
        }, 200);
      }
    }
  };

  const startListening = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError(
        "Voice transcription is not supported in this browser. Please use Chrome or Edge."
      );
      return;
    }

    setError("");
    shouldListenRef.current = true;
    setIsListening(true);
    lastSpeechTimeRef.current = Date.now();
    lastResultTimeRef.current = Date.now();
    sessionStartRef.current = Date.now();
    sessionFinalCountRef.current = 0;
    setInterimTranscript("");

    startRecognitionSession();
    startWatchdog();
  };

  const stopListening = () => {
    shouldListenRef.current = false;
    clearTimeout(restartTimeoutRef.current);
    clearTimeout(cycleTimeoutRef.current);
    stopWatchdog();

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.abort();
      } catch (e) {}
      recognitionRef.current = null;
    }
    setIsListening(false);
    setIsAudioActive(false);
    setIsSpeaking(false);
    setInterimTranscript("");
  };

  const processTranscript = async () => {
    const fullTranscript = `${transcript}${
      interimTranscript ? (transcript ? " " : "") + interimTranscript : ""
    }`.trim();

    if (!fullTranscript) {
      setError("Please speak or enter encounter details before processing.");
      return;
    }

    stopListening();
    setIsProcessing(true);
    setError("");

    try {
      const report = await generateVoiceNote(fullTranscript, catalog);
      onGenerated(report);
      setIsProcessed(true);
    } catch (processingError: any) {
      setError(
        processingError?.message ||
          "Unable to process the transcript into a chart note. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const reset = () => {
    stopListening();
    setTranscript("");
    transcriptRef.current = "";
    setInterimTranscript("");
    setIsProcessed(false);
    setError("");
    setDurationSeconds(0);
  };

  const displayTranscript = `${transcript}${
    interimTranscript ? (transcript ? " " : "") + interimTranscript : ""
  }`;

  const handleCopy = () => {
    const fullText = displayTranscript.trim();
    if (fullText) {
      navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const wordCount = displayTranscript.trim()
    ? displayTranscript.trim().split(/\s+/).filter(Boolean).length
    : 0;

  const formatTimer = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 pt-4">
      {/* Header and Live Status */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borderPrimary pb-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-textPrimary">
              Voice Note Generation
            </h2>
            {isListening && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-600 dark:bg-red-950/40 dark:text-red-400">
                <span className="h-2 w-2 animate-ping rounded-full bg-red-600"></span>
                Live (Continuous Auto-Refreshing)
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-textSecondary sm:text-sm">
            Continuous doctor-patient transcription with multi-speaker turn formatting and auto-stall recovery.
          </p>
        </div>

        {/* Real-time Voice Activity Indicator and Duration */}
        {isListening && (
          <div className="flex items-center gap-3 rounded-lg border border-borderPrimary bg-surfacePrimary px-3 py-1.5 shadow-sm">
            <div className="flex items-center gap-1.5">
              <MdGraphicEq
                className={`text-lg transition-colors ${
                  isSpeaking
                    ? "text-green-500"
                    : isAudioActive
                    ? "text-brand-500"
                    : "text-textSecondary"
                }`}
              />
              <div className="flex h-5 items-end gap-0.5">
                {[
                  { base: "h-2", active: "h-4" },
                  { base: "h-3", active: "h-5" },
                  { base: "h-1.5", active: "h-3.5" },
                  { base: "h-3.5", active: "h-5" },
                  { base: "h-2", active: "h-4" },
                  { base: "h-4", active: "h-5" },
                  { base: "h-2.5", active: "h-4.5" },
                  { base: "h-1.5", active: "h-3" },
                ].map((bar, idx) => (
                  <span
                    key={idx}
                    className={`w-1 rounded-t transition-all duration-150 ${
                      isSpeaking
                        ? `bg-green-500 ${bar.active} animate-pulse`
                        : isAudioActive
                        ? `bg-brand-500 ${bar.active}`
                        : `bg-textSecondary/40 ${bar.base}`
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs font-medium text-textSecondary">
                {isSpeaking ? "Speaking" : isAudioActive ? "Hearing" : "Listening"}
              </span>
            </div>
            <span className="text-xs font-mono font-bold text-textPrimary">
              {formatTimer(durationSeconds)}
            </span>
          </div>
        )}
      </div>

      {/* Main Controls & Dialogue Settings Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {!isListening ? (
            <button
              type="button"
              onClick={startListening}
              className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-brand-600 hover:shadow-lg active:scale-95"
            >
              <MdMic className="text-lg" /> Start Transcription
            </button>
          ) : (
            <button
              type="button"
              onClick={stopListening}
              className="flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-red-700 hover:shadow-lg active:scale-95"
            >
              <MdStop className="text-lg" /> Stop Transcription
            </button>
          )}

          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1.5 rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 py-2.5 text-sm font-semibold text-textSecondary transition hover:text-brand-500 hover:border-brand-500"
          >
            <MdRefresh className="text-base" /> Clear
          </button>

          <button
            type="button"
            onClick={handleCopy}
            disabled={!displayTranscript.trim()}
            className="flex items-center gap-1.5 rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 py-2.5 text-sm font-semibold text-textSecondary transition hover:text-brand-500 hover:border-brand-500 disabled:opacity-40"
          >
            {copied ? (
              <>
                <MdCheck className="text-green-600 text-base" />
                <span className="text-green-600">Copied</span>
              </>
            ) : (
              <>
                <MdContentCopy className="text-base" /> Copy
              </>
            )}
          </button>
        </div>


      </div>

      {/* Transcript Textarea Area */}
      <div className="relative flex min-h-[240px] flex-1 flex-col">
        <textarea
          value={displayTranscript}
          onChange={(event) => {
            setTranscript(event.target.value);
            transcriptRef.current = event.target.value;
            setInterimTranscript("");
          }}
          placeholder="Speak into your microphone. Both doctor and patient voices will be transcribed continuously in real time. You can also edit this text manually at any time..."
          className="h-full min-h-[220px] w-full resize-y rounded-xl border border-borderPrimary bg-surfacePrimary p-4 text-sm leading-relaxed text-textPrimary shadow-inner outline-none transition-colors focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          disabled={isProcessing}
        />

        {/* Footer Statistics Bar */}
        <div className="mt-2 flex items-center justify-between text-xs text-textSecondary">
          <div className="flex items-center gap-1.5">
            <MdInfoOutline className="text-sm" />
            <span>
              Continuous rolling refresh & watchdog active. Long dictations won't freeze.
            </span>
          </div>
          <div className="font-medium">
            {wordCount} {wordCount === 1 ? "word" : "words"} • {displayTranscript.length} chars
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Action Footer */}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-borderPrimary pt-3">
        <button
          type="button"
          onClick={() => {
            stopListening();
            onCancel();
          }}
          className="rounded-xl border border-borderPrimary bg-surfaceSecondary px-5 py-2.5 text-sm font-semibold text-textSecondary transition hover:text-brand-500"
        >
          Manual Note
        </button>

        <div className="flex items-center gap-3">
          {!isProcessed ? (
            <button
              type="button"
              onClick={processTranscript}
              disabled={isProcessing || !displayTranscript.trim()}
              className="flex items-center gap-2 rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-brand-600 hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MdSend /> {isProcessing ? "Processing AI Note..." : "Process Transcript"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onCreateNote}
              className="flex items-center gap-2 rounded-xl bg-green-600 px-6 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-green-700 active:scale-95"
            >
              Create Note
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceNoteGeneration;